import React, { useEffect, useState } from "react";
import {
  Card,
  Row,
  Col,
  Typography,
  Button,
  Input,
  Select,
  Table,
  Space,
  Tooltip,
  message,
  Popconfirm,
  Modal,
} from "antd";
import {
  PlusOutlined,
  FileExcelOutlined,
  SearchOutlined,
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
  PrinterOutlined,
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import axiosInstance, { baseURL } from "../../configs"; // sesuaikan path
import dayjs from "dayjs";

const { Title, Text } = Typography;
const { Option } = Select;

const defaultPageSize = 20;

export default function BrowseOpname() {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: defaultPageSize,
    total: 0,
  });

  // filter
  const [searchName, setSearchName] = useState("");
  const [searchCode, setSearchCode] = useState("");

  const [deletingId, setDeletingId] = useState(null);

  // Print Preview State
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");

  const fetchItems = async (page = 1, pageSize = defaultPageSize) => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("/stock-opname", {
        params: {
          page,
          limit: pageSize,
          item_name: searchName || undefined,
          item_code: searchCode || undefined,
        },
      });

      const raw = res.data || {};
      const payload = raw.data || raw;
      const rows =
        payload.items ||
        payload.rows ||
        payload.data ||
        (Array.isArray(payload) ? payload : []);

      const total =
        payload.total ||
        payload.count ||
        payload.pagination?.total ||
        rows.length;

      setItems(rows);
      setPagination({
        current: page,
        pageSize,
        total: total || 0,
      });
    } catch (err) {
      console.error(err);
      message.error("Gagal memuat daftar opname");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems(1, defaultPageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    fetchItems(1, pagination.pageSize);
  };

  const handleResetFilter = () => {
    setSearchName("");
    setSearchCode("");
    fetchItems(1, defaultPageSize);
  };

  const handleTableChange = (pager) => {
    fetchItems(pager.current, pager.pageSize);
  };

  const handleExportExcel = () => {
    window.open(`${baseURL}/stock-opname/export`, "_blank");
  };

  const handlePrint = async (id) => {
    try {
      const res = await axiosInstance.get(`/stock-opname/${id}/print-data`);
      const data = res.data || [];

      // Build HTML
      let rowsHtml = "";
      data.forEach((item, index) => {
        const parts = (item.lokasi_lot || "").split(".");
        const lotPrefix = parts[0] || "";
        const lotNo = parts[1] || "";

        // Remove .00 by converting to number (and implicit toString handles it, or explicit integer)
        const qty = Number(item.qty_kemasan || 0);
        const isi = Number(item.isi_per_unit || 0);
        const totalPcs = qty * isi;

        rowsHtml += `
          <tr>
            <td style="text-align:center">${index + 1}</td>
            <td>${item.importir || "-"}</td>
            <td>${item.item_name || item.item_code}</td>
            <td style="text-align:center">${item.nopen || "-"}</td>
            <td style="text-align:center">${item.doc_date ? dayjs(item.doc_date).format("DD/MM/YYYY") : "-"}</td>
            <td style="text-align:center">${qty}</td>
            <td style="text-align:center">${isi}</td>
            <td style="text-align:center">${totalPcs}</td>
            <td style="text-align:center">${lotPrefix}</td>
            <td style="text-align:center">${lotNo}</td>
          </tr>
        `;
      });

      const html = `
        <html>
          <head>
            <title>Cetak Stock Opname</title>
            <style>
              body { font-family: 'Times New Roman', Times, serif; font-size: 11px; margin: 20px; }
              h2, h3 { text-align: center; margin: 2px; text-transform: uppercase; }
              table { width: 100%; border-collapse: collapse; margin-top: 15px; }
              th, td { border: 1px solid #000; padding: 4px; vertical-align: middle; }
              th { background-color: #eaeaea; text-align: center; font-weight: bold; }
            </style>
          </head>
          <body>
            <h3>DATA HASIL STOCK OPNAME</h3>
            <h3>PLB PASIFIK LOGISTIK BERIKAT</h3>
            <br/>
            <table>
              <thead>
                <tr>
                  <th width="30">NO</th>
                  <th>IMPORTIR</th>
                  <th>NAMA BARANG</th>
                  <th>NOPEN 1.6</th>
                  <th width="75">TGL NOPEN 1.6</th>
                  <th width="50">CS/CT</th>
                  <th width="40">ISI</th>
                  <th width="60">BOTOL/ PCE</th>
                  <th width="40">LOT</th>
                  <th width="40">NO LOT</th>
                </tr>
              </thead>
              <tbody>
                ${rowsHtml}
              </tbody>
            </table>
          </body>
        </html>
      `;

      // Open Modal Preview
      setPreviewHtml(html);
      setPreviewOpen(true);
    } catch (err) {
      console.error(err);
      message.error("Gagal mencetak data");
    }
  };

  const handlePrintFrame = () => {
    const iframe = document.getElementById("print-preview-frame");
    if (iframe && iframe.contentWindow) {
      iframe.contentWindow.print();
    }
  };

  const handleDelete = async (record) => {
    setDeletingId(record.id_stock_opname);
    try {
      await axiosInstance.delete(`/stock-opname/${record.id_stock_opname}`);
      message.success("Opname berhasil dihapus");
      // reload current page
      fetchItems(pagination.current, pagination.pageSize);
    } catch (err) {
      console.error(err);
      message.error("Gagal menghapus Opname");
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (value) =>
    value ? dayjs(value).format("DD-MM-YYYY") : "-";

  const columns = [
    {
      title: "NO",
      dataIndex: "index",
      width: 70,
      align: "center",
      render: (text, record, index) =>
        (pagination.current - 1) * pagination.pageSize + index + 1,
    },
    {
      title: "NO OPNAME",
      dataIndex: "opname_number",
      width: 130,
    },
    {
      title: "TANGGAL OPNAME",
      dataIndex: "opname_date",
      width: 140,
      render: (value) => formatDate(value),
    },
    {
      title: "AKSI",
      key: "action",
      width: 160,
      align: "center",
      render: (_, record) => (
        <Space>
          <Tooltip title="Detil & Edit Stock Opname">
            <Link to={`/inventory/stock-opname/edit/${record.id_stock_opname}`}>
              <Button
                size="small"
                type="primary"
                icon={<EditOutlined />}
                style={{
                  background: "#3699FF",
                  borderColor: "#3699FF",
                }}
              />
            </Link>
          </Tooltip>

          <Tooltip title="Cetak Hasil Opname">
            <Button
              size="small"
              icon={<PrinterOutlined />}
              onClick={() => handlePrint(record.id_stock_opname)}
              style={{
                background: "#8950FC",
                borderColor: "#8950FC",
                color: "#fff"
              }}
            />
          </Tooltip>

          <Popconfirm
            title="Hapus Adjustment"
            description={`Yakin ingin menghapus "${record.adjustment_number || record.adjustment_number
              }"?`}
            okText="Ya, Hapus"
            cancelText="Batal"
            okButtonProps={{
              danger: true,
              loading: deletingId === record.id_incoming,
            }}
            onConfirm={() => handleDelete(record)}
          >
            <Tooltip title="Hapus">
              <Button
                danger
                size="small"
                icon={<DeleteOutlined />}
                loading={deletingId === record.id_incoming}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card className="kt-portlet" bodyStyle={{ padding: 24 }}>
      {/* HEADER TITLE + ACTIONS */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col xs={24} md={12} style={{ marginBottom: { xs: 16, md: 0 } }}>
          <Title level={3} style={{ marginBottom: 4 }}>
            Browse Opname
          </Title>
          <Text type="secondary">
            Kelola daftar opname untuk kebutuhan inventory & kepabeanan.
          </Text>
        </Col>
        <Col xs={24} md={12} style={{ textAlign: { xs: 'left', md: 'right' } }}>
          <Space wrap>
            <Link to="/other/opname/add">
              <Button
                type="primary"
                icon={<PlusOutlined />}
                style={{
                  background: "#1BC5BD",
                  borderColor: "#1BC5BD",
                  fontWeight: 600,
                }}
              >
                Tambah
              </Button>
            </Link>
            <Button
              type="primary"
              icon={<FileExcelOutlined />}
              onClick={handleExportExcel}
              style={{
                background: "#1E1EFA",
                borderColor: "#1E1EFA",
                fontWeight: 600,
              }}
            >
              Excel
            </Button>
          </Space>
        </Col>
      </Row>

      {/* FILTER BAR */}
      <Card
        bodyStyle={{ padding: 16 }}
        style={{
          marginBottom: 24,
          borderRadius: 12,
          boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
          border: "1px solid #F3F6F9",
        }}
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <Text strong>Cari Berdasarkan Nama Barang</Text>
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder="Cari Item..."
              style={{ marginTop: 4 }}
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
              onPressEnter={handleSearch}
            />
          </Col>

          <Col xs={24} md={12}>
            <Text strong>Cari Berdasarkan Kode Barang</Text>
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder="Cari kode..."
              style={{ marginTop: 4 }}
              value={searchCode}
              onChange={(e) => setSearchCode(e.target.value)}
              onPressEnter={handleSearch}
            />
          </Col>

          <Col xs={24} style={{ textAlign: "right" }}>
            <Space>
              <Button icon={<ReloadOutlined />} onClick={handleResetFilter}>
                Reset
              </Button>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={handleSearch}
                style={{
                  background: "#8950FC",
                  borderColor: "#8950FC",
                }}
              >
                Apply Filter
              </Button>
            </Space>
          </Col>
        </Row>

        <Table
          style={{ marginTop: 36 }}
          rowKey="id_item"
          dataSource={items}
          columns={columns}
          loading={loading}
          scroll={{ x: 'max-content' }}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showTotal: (total, range) =>
              `Total ${total} data | ${range[0]}-${range[1]}`,
          }}
          onChange={handleTableChange}
        />
      </Card>

      {/* PRINT PREVIEW MODAL */}
      <Modal
        title="Preview Cetak Stock Opname"
        open={previewOpen}
        onCancel={() => setPreviewOpen(false)}
        width={1000}
        style={{ top: 20 }}
        bodyStyle={{ padding: 0, height: "80vh", overflow: "hidden" }}
        footer={[
          <Button key="close" onClick={() => setPreviewOpen(false)}>
            Tutup
          </Button>,
          <Button
            key="print"
            type="primary"
            icon={<PrinterOutlined />}
            onClick={handlePrintFrame}
          >
            Cetak
          </Button>,
        ]}
      >
        <iframe
          id="print-preview-frame"
          srcDoc={previewHtml}
          style={{ width: "100%", height: "100%", border: "none" }}
          title="Print Preview"
        />
      </Modal>
    </Card>
  );
}
